/**
 * Centralized Nivo chart theme configurations.
 * Consolidates the duplicated themeGraph objects from all chart components.
 */

// ============================================
// Theme Colors
// ============================================

import { CHART_LABEL_FONT_SIZES } from './chartConfig';
import { brandTokens } from '@softwareiq/brand-ui';

export const getChartFontFamily = (theme) => (
  theme?.typography?.fontFamily || brandTokens.fontFamily
);

const THEME_COLORS = {
  light: {
    text: 'black',
    gridLine: 'rgba(0, 0, 0, 0.1)',
    crosshair: 'rgba(0, 0, 0, 0.8)',
    tooltipBackground: '#FFFFFF',
  },
  dark: {
    text: 'white',
    gridLine: 'rgba(255, 255, 255, 0.1)',
    crosshair: 'rgba(255, 255, 255, 0.8)',
    tooltipBackground: '#323232',
  },
};

// ============================================
// Main Theme Factory
// ============================================

/**
 * Creates a Nivo theme configuration based on MUI theme
 * @param {Object} theme - MUI theme object
 * @param {Object} options - Optional customizations
 * @param {number} options.gridOpacity - Grid line opacity (default uses standard)
 * @param {boolean} options.boldAxisLabels - Whether axis labels should be bold (default: true)
 * @returns {Object} Nivo theme configuration
 */
export const getNivoTheme = (theme, options = {}) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? THEME_COLORS.dark : THEME_COLORS.light;
  const { boldAxisLabels = true, gridOpacity } = options;

  // Allow custom grid opacity override
  const gridLineStroke = gridOpacity
    ? isDarkMode
      ? `rgba(255, 255, 255, ${gridOpacity})`
      : `rgba(0, 0, 0, ${gridOpacity})`
    : colors.gridLine;

  return {
    text: { fontFamily: getChartFontFamily(theme) },
    textColor: colors.text,
    grid: {
      line: {
        stroke: gridLineStroke,
      },
    },
    axis: {
      legend: {
        text: {
          fill: colors.text,
          fontWeight: boldAxisLabels ? 'bold' : 'normal',
        },
      },
      ticks: {
        text: {
          fill: colors.text,
        },
      },
    },
    markers: {
      lineColor: colors.text,
      textColor: colors.text,
    },
    tooltip: {
      container: {
        background: colors.tooltipBackground,
        color: colors.text,
      },
    },
    legends: {
      text: {
        fill: colors.text,
      },
    },
    crosshair: {
      line: {
        stroke: colors.crosshair,
        strokeWidth: 0.5,
        strokeDasharray: '4 4',
      },
    },
  };
};

/**
 * Creates a Nivo theme with scatter plot-specific settings
 * @param {Object} theme - MUI theme object
 * @returns {Object} Nivo theme configuration for scatter plots
 */
export const getScatterTheme = (theme) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const baseTheme = getNivoTheme(theme, { gridOpacity: isDarkMode ? 0.2 : 0.1 });

  return {
    ...baseTheme,
    axis: {
      ...baseTheme.axis,
      ticks: {
        text: {
          fill: isDarkMode ? 'white' : 'black',
          fontWeight: 'bold',
        },
      },
    },
    colors: {
      scheme: isDarkMode ? 'paired' : 'pastel1',
    },
    annotations: {
      text: {
        fill: theme.palette.text.secondary,
        outlineColor: isDarkMode ? 'rgb(30,30,30)' : '#f2f2f2',
        fontWeight: '600',
        fontSize: `${CHART_LABEL_FONT_SIZES.ANNOTATION}px`,
        outlineWidth: 2,
      },
      link: {
        strokeWidth: 0,
        outlineWidth: 0,
      },
      outline: {
        outlineWidth: 0,
        strokeWidth: 0,
        stroke: isDarkMode ? 'white' : 'black',
      },
    },
  };
};

/**
 * Creates a Nivo theme for bar charts
 * @param {Object} theme - MUI theme object
 * @returns {Object} Nivo theme configuration for bar charts
 */
export const getBarTheme = (theme) => {
  const baseTheme = getNivoTheme(theme);

  // Bar charts don't typically use crosshairs
  const { crosshair, ...restTheme } = baseTheme;

  return restTheme;
};

// ============================================
// Legacy Export (for gradual migration)
// ============================================

/**
 * @deprecated Use getNivoTheme(theme) instead
 * Creates the themeGraph object that was duplicated across chart files
 */
export const createThemeGraph = (isDarkMode) => {
  const colors = isDarkMode ? THEME_COLORS.dark : THEME_COLORS.light;

  return {
    text: { fontFamily: brandTokens.fontFamily },
    textColor: colors.text,
    grid: {
      line: {
        stroke: colors.gridLine,
      },
    },
    axis: {
      legend: {
        text: {
          fill: colors.text,
          fontWeight: 'bold',
        },
      },
      ticks: {
        text: {
          fill: colors.text,
        },
      },
    },
    markers: {
      lineColor: colors.text,
      textColor: colors.text,
    },
    tooltip: {
      container: {
        background: colors.tooltipBackground,
        color: colors.text,
      },
    },
    legends: {
      text: {
        fill: colors.text,
      },
    },
    crosshair: {
      line: {
        stroke: colors.crosshair,
        strokeWidth: 0.5,
        strokeDasharray: '4 4',
      },
    },
  };
};

export default getNivoTheme;
